/**
 * Task due times: comparing, rendering and building them.
 *
 * ⚠️ THE ONE RULE, which every function here exists to keep.
 *
 * A due time is sent as a **wall clock with no zone** ('2026-08-27 15:00') and
 * resolved server-side through the **ASSIGNEE's** timezone, because a due time
 * is a claim on the working day of whoever has to act. The same string is three
 * different instants:
 *
 *   assigned to London  -> 2026-08-27T14:00:00.000Z
 *   assigned to Mumbai  -> 2026-08-27T09:30:00.000Z
 *   assigned to NY      -> 2026-08-27T19:00:00.000Z
 *
 * It comes back as `{ utc, timeZone, local, offset }` and **nothing here
 * converts it**. `local` is a RENDERING, not a value: comparing or sorting on it
 * silently mixes three timelines and looks right while being wrong. Every
 * comparison in this file goes through `due.utc`, and `local` is only ever
 * sliced as text.
 *
 * Nothing here throws. These helpers render whole lists, and one malformed row
 * from an import must not blank the page.
 */
import { isRealDate } from '@/components/catalog/form-utils';
import type { TaskDue, TaskKind, TaskState } from '@/lib/types';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/* ------------------------------------------------------------------ *
 * Comparing — always against `utc`.
 * ------------------------------------------------------------------ */

/**
 * The instant a due time refers to, in epoch ms, or `null` when there isn't one.
 *
 * `Date.parse` on `due.utc` is the only parse in this module that produces an
 * instant. Never `new Date(due.local)` — that string carries no zone, so the
 * runtime reads it as the VIEWER's local time, which is precisely the timeline
 * we are trying not to be on.
 */
function dueInstant(due: TaskDue | null): number | null {
  if (!due || typeof due.utc !== 'string') return null;
  const ms = Date.parse(due.utc);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * The clock to measure against.
 *
 * Callers pass a `now` so this is testable and so a list can be rendered
 * against a **skew-corrected** clock: `/tasks/brief` returns the server's `now`
 * for exactly that reason, and laptop clocks are routinely minutes out. An
 * unusable `Date` falls back to the real clock rather than poisoning every
 * comparison with NaN.
 */
function clockMs(now?: Date): number {
  if (!now) return Date.now();
  const ms = now.getTime();
  return Number.isFinite(ms) ? ms : Date.now();
}

/** Is this due time in the past? Compares `utc` against the clock. Null due = false. */
export function isOverdue(due: TaskDue | null, now?: Date): boolean {
  const at = dueInstant(due);
  if (at === null) return false;
  return at < clockMs(now);
}

/** Milliseconds until due; negative when overdue; null when undated. */
export function msUntilDue(due: TaskDue | null, now?: Date): number | null {
  const at = dueInstant(due);
  if (at === null) return null;
  return at - clockMs(now);
}

/* ------------------------------------------------------------------ *
 * Rendering — Intl only, no hand-rolled offset maths.
 * ------------------------------------------------------------------ */

/**
 * Formatters are cached because a task list builds one per row otherwise, and
 * `Intl.DateTimeFormat` construction is the expensive half of formatting.
 *
 * A bad zone string makes the constructor throw, so the cache stores `null` for
 * one that failed and the caller falls back to slicing `local`.
 */
const displayFormatters = new Map<string, Intl.DateTimeFormat | null>();
const wallClockFormatters = new Map<string, Intl.DateTimeFormat | null>();

function cachedFormatter(
  cache: Map<string, Intl.DateTimeFormat | null>,
  timeZone: string,
  build: () => Intl.DateTimeFormat,
): Intl.DateTimeFormat | null {
  const hit = cache.get(timeZone);
  if (hit !== undefined) return hit;
  let made: Intl.DateTimeFormat | null;
  try {
    made = build();
  } catch {
    made = null;
  }
  cache.set(timeZone, made);
  return made;
}

/** Reader-facing rendering of an instant, in the zone whose day it belongs to. */
function displayFormatter(timeZone: string): Intl.DateTimeFormat | null {
  return cachedFormatter(
    displayFormatters,
    timeZone,
    () =>
      new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
      }),
  );
}

/**
 * A stable, comparable wall clock for one instant in one zone.
 *
 * The locale is pinned so two zones' renderings can be compared as strings —
 * the viewer's locale must not decide whether two clocks match.
 */
function wallClockFormatter(timeZone: string): Intl.DateTimeFormat | null {
  return cachedFormatter(
    wallClockFormatters,
    timeZone,
    () =>
      new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone,
      }),
  );
}

function wallClockAt(timeZone: string, instant: number): string | null {
  const formatter = wallClockFormatter(timeZone);
  if (!formatter) return null;
  try {
    return formatter.format(instant);
  } catch {
    return null;
  }
}

/**
 * Splits 'YYYY-MM-DD HH:MM:SS' into the two halves a form edits.
 *
 * Pure text work. Both separators the API accepts are tolerated, seconds are
 * dropped, and anything else is `null` rather than a guess.
 */
function splitLocal(local: unknown): { date: string; time: string } | null {
  if (typeof local !== 'string') return null;
  const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(?::\d{2})?/.exec(local.trim());
  if (!match) return null;
  const [, date = '', time = ''] = match;
  return { date, time };
}

/** How a foreign due time names whose day it is: '(+05:30, Asia/Kolkata)'. */
function zoneSuffix(due: TaskDue): string {
  const zone = typeof due.timeZone === 'string' ? due.timeZone.trim() : '';
  const offset = typeof due.offset === 'string' ? due.offset.trim() : '';
  if (zone && offset) return ` (${offset}, ${zone})`;
  if (zone) return ` (${zone})`;
  if (offset) return ` (${offset})`;
  return '';
}

/**
 * True when the due time is on a different clock from the viewer's.
 *
 * Not a string comparison on the zone names. Asia/Calcutta and Asia/Kolkata are
 * the same clock under two spellings, and Europe/London and Europe/Lisbon show
 * the same wall clock all year — annotating those is noise. So the test is the
 * one the reader actually cares about: rendered at this instant, do the two
 * zones read the same? That handles aliases and DST for free.
 *
 * The unknown cases lean towards labelling. Hiding the zone is what misleads a
 * reader; showing one they did not need is merely redundant.
 */
export function isForeignZone(due: TaskDue | null, viewerTimeZone: string): boolean {
  if (!due) return false;
  const dueZone = typeof due.timeZone === 'string' ? due.timeZone.trim() : '';
  const viewer = typeof viewerTimeZone === 'string' ? viewerTimeZone.trim() : '';
  if (!dueZone) return false;
  if (!viewer) return true;
  if (dueZone === viewer) return false;

  const at = dueInstant(due);
  if (at === null) return true;
  const theirs = wallClockAt(dueZone, at);
  const ours = wallClockAt(viewer, at);
  if (theirs === null || ours === null) return true;
  return theirs !== ours;
}

/**
 * Render a due time for a reader: the assignee's wall clock, plus the zone when
 * it is NOT the viewer's own — "15:00" to a Mumbai viewer, "15:00 (+05:30,
 * Asia/Kolkata)" to a London one, because otherwise a London reader silently
 * assumes 15:00 means their afternoon.
 *
 * The wall clock is produced by formatting the real instant (`utc`) THROUGH the
 * assignee's zone, which is the same clock `local` spells out and is the only
 * way to get there without hand-rolling offset maths. `local` is the fallback
 * for a row the runtime cannot parse — sliced as text, never parsed as a date.
 */
export function formatDue(due: TaskDue | null, viewerTimeZone: string): string {
  if (!due) return '—';
  const suffix = isForeignZone(due, viewerTimeZone) ? zoneSuffix(due) : '';

  const at = dueInstant(due);
  const zone = typeof due.timeZone === 'string' ? due.timeZone.trim() : '';
  if (at !== null && zone) {
    const formatter = displayFormatter(zone);
    if (formatter) {
      try {
        return `${formatter.format(at)}${suffix}`;
      } catch {
        /* falls through to the text rendering below */
      }
    }
  }

  const parts = splitLocal(due.local);
  if (parts) return `${parts.date} ${parts.time}${suffix}`;
  return '—';
}

/** One instance, reused: relative formatting happens per row too. */
const relativeFormatter: Intl.RelativeTimeFormat | null = (() => {
  try {
    return new Intl.RelativeTimeFormat(undefined, { numeric: 'always' });
  } catch {
    return null;
  }
})();

/**
 * Relative, human: "in 2 hours", "3 days ago", "just now". Null due -> null.
 *
 * `numeric: 'always'` on purpose — "tomorrow" from a 24-hour delta is a lie
 * whenever the due time sits on someone else's calendar day, and this string is
 * shown beside due times that often do.
 *
 * `now` is optional so a caller can pass the **skew-corrected** clock from
 * `/tasks/brief`; a laptop several minutes out otherwise reports things overdue
 * that are not.
 */
export function relativeDue(due: TaskDue | null, now?: Date): string | null {
  const diff = msUntilDue(due, now);
  if (diff === null) return null;

  const magnitude = Math.abs(diff);
  if (magnitude < 45_000) return 'just now';

  let value: number;
  let unit: Intl.RelativeTimeFormatUnit;
  if (magnitude < 45 * MINUTE_MS) {
    value = Math.round(magnitude / MINUTE_MS);
    unit = 'minute';
  } else if (magnitude < 22 * HOUR_MS) {
    value = Math.round(magnitude / HOUR_MS);
    unit = 'hour';
  } else if (magnitude < 26 * DAY_MS) {
    value = Math.round(magnitude / DAY_MS);
    unit = 'day';
  } else if (magnitude < 320 * DAY_MS) {
    value = Math.round(magnitude / (30.44 * DAY_MS));
    unit = 'month';
  } else {
    value = Math.round(magnitude / (365.25 * DAY_MS));
    unit = 'year';
  }
  const signed = Math.max(1, value) * (diff < 0 ? -1 : 1);

  if (!relativeFormatter) {
    const plural = Math.abs(signed) === 1 ? unit : `${unit}s`;
    return signed < 0 ? `${-signed} ${plural} ago` : `in ${signed} ${plural}`;
  }
  try {
    return relativeFormatter.format(signed, unit);
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ *
 * Whose clock is this, really?
 * ------------------------------------------------------------------ */

/**
 * A zone's offset from UTC right now, in minutes. `null` when the runtime
 * cannot format that zone.
 *
 * ⚠️ **COMPARE OFFSETS, NEVER ZONE NAMES.** `Asia/Calcutta` and `Asia/Kolkata`
 * are the same clock under two spellings, and the server's own default is the
 * second while some browsers still resolve the first — so a string comparison
 * reports every Indian user as being in the wrong place. This module already
 * records that `Asia/Kolkata` is missing from `Intl.supportedValuesOf` while
 * formatting with it works fine; the names are not a reliable identity.
 *
 * ⚠️ **"Right now", not in general.** Two zones can share an offset today and
 * differ in April — London and Lisbon do. That is correct for what this is for:
 * the question is whether the time somebody is typing lands where they think it
 * does, which is a question about today.
 */
function zoneOffsetMinutes(timeZone: string, at: Date = new Date()): number | null {
  const parts = wallClockFormatter(timeZone)?.formatToParts(at);
  if (!parts) return null;
  const value = (type: string): number => Number(parts.find((part) => part.type === type)?.value);
  const asUtc = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    /*
     * ⚠️ `% 24` because `hourCycle: 'h23'` can still render midnight as 24 in
     * some locales, and `Date.UTC` would roll that into the NEXT DAY — a silent
     * 24-hour error on exactly the boundary this is measuring.
     */
    value('hour') % 24,
    value('minute'),
  );
  if (Number.isNaN(asUtc)) return null;
  /*
   * ⚠️ **BOTH SIDES ARE FLOORED TO THE MINUTE, and asking for seconds instead
   * is the version that silently returns `null` for every zone.**
   * `wallClockFormatter` requests year, month, day, hour and minute and NOTHING
   * ELSE, so `formatToParts` emits no `second` part: reading one gives
   * `Number(undefined)` -> `NaN`, `Date.UTC` yields `NaN`, and this function
   * answers `null` — which `recordedZoneMisreadsThisMachine` correctly reads as
   * "cannot tell" and reports as **no mismatch, for every zone pair there is**.
   *
   * That was written, and it was caught by running the helper against a real
   * pair of zones rather than by reading it: a warning that never fires looks
   * exactly like a warning with nothing to warn about. The floor is what makes
   * the subtraction a whole number of minutes on both sides.
   */
  const flooredAt = Math.floor(at.getTime() / MINUTE_MS) * MINUTE_MS;
  return Math.round((asUtc - flooredAt) / MINUTE_MS);
}

/**
 * Is the user's RECORDED timezone a different clock from the one their machine
 * is actually on?
 *
 * 🔴 **THIS IS THE CONDITION BEHIND "I SET IT FOR FIVE MINUTES AWAY AND IT SAID
 * OVERDUE", AND IT IS NOT THE OBVIOUS ONE.** Reported 4 September 2026 and
 * reproduced against production on the 11th.
 *
 * A due time is sent as a zone-less wall clock and resolved through the
 * ASSIGNEE's recorded zone. Measured on production: **all 15 staff accounts
 * carry `Asia/Kolkata`, every one of them the column default that nobody has
 * ever changed.** So somebody working in London types `02:09`, meaning their
 * own clock, and the server correctly reads it as 02:09 in India — an instant
 * that passed four and a half hours ago:
 *
 * ```
 * typed   2026-09-11 02:09        (her local, five minutes ahead)
 * stored  2026-09-10T20:39:00Z    (that wall clock, read in Asia/Kolkata)
 * result  265 minutes in the past -> overdue on arrival
 * ```
 *
 * ⚠️ **COMPARING THE ASSIGNEE'S ZONE WITH THE VIEWER'S WOULD NEVER FIRE.** Both
 * come from `d_users.timezone`, so they agree while every row holds the same
 * default — which is exactly the state that produces the bug. The only
 * detectable disagreement is between what the RECORD says and what the MACHINE
 * says, and that is what this asks.
 *
 * ⚠️ **It answers `false` when it cannot tell**, never `true`. An unknown zone
 * or a browser that will not report one is not evidence of a mismatch, and a
 * warning nobody can act on is worse than silence.
 *
 * **The real fix is the DATA** — set each person's actual zone. This exists so
 * that until then the failure is visible at the moment it is created rather
 * than discovered as a task that was born late.
 */
export function recordedZoneMisreadsThisMachine(recordedTimeZone: string | null | undefined): boolean {
  const recorded = typeof recordedTimeZone === 'string' ? recordedTimeZone.trim() : '';
  if (!recorded) return false;
  let browser = '';
  try {
    browser = new Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    return false;
  }
  if (!browser) return false;
  const at = new Date();
  const recordedOffset = zoneOffsetMinutes(recorded, at);
  const browserOffset = zoneOffsetMinutes(browser, at);
  if (recordedOffset === null || browserOffset === null) return false;
  return recordedOffset !== browserOffset;
}

/**
 * The instant a typed wall clock will actually fall due, rendered on the
 * machine's own clock — so somebody can see it before they save.
 *
 * `null` when the parts are incomplete, when the zone is unusable, or when
 * there is nothing worth saying. Never throws.
 */
export function dueLandsAt(
  date: string,
  time: string,
  assigneeTimeZone: string,
  at: Date = new Date(),
): { instant: Date; alreadyPast: boolean } | null {
  const wall = toDueAtString(date, time);
  if (wall === null) return null;
  const offset = zoneOffsetMinutes(assigneeTimeZone, at);
  if (offset === null) return null;
  const [day = '', clock = ''] = wall.split(' ');
  const [y, m, d] = day.split('-').map(Number);
  const [hh, mm] = clock.split(':').map(Number);
  if ([y, m, d, hh, mm].some((part) => !Number.isFinite(part))) return null;
  /*
   * The wall clock read IN that zone: take it as if it were UTC, then subtract
   * the zone's offset. This mirrors what the server does and is the only
   * arithmetic here — everything else in this module refuses to convert.
   */
  const instant = new Date(Date.UTC(y as number, (m as number) - 1, d as number, hh as number, mm as number) - offset * MINUTE_MS);
  if (Number.isNaN(instant.getTime())) return null;
  return { instant, alreadyPast: instant.getTime() < at.getTime() };
}

/* ------------------------------------------------------------------ *
 * Building — the wall clock that goes back over the wire.
 * ------------------------------------------------------------------ */

/**
 * Build the `dueAt` string a create/update body takes, from the parts a form
 * collects. Returns null when the date is absent, and THROWS NOTHING — an
 * invalid combination returns null so the caller can refuse rather than send.
 *
 * The output is 'YYYY-MM-DD HH:MM' and carries **no zone**: no trailing `Z`, no
 * offset. Those are instants, and the API refuses them, because an instant
 * would make this client decide whose clock the task is on — the decision that
 * has to live server-side, against the assignee's zone.
 *
 * The calendar is validated as well as the shape, via the same `isRealDate` the
 * catalog forms use: '2026-02-30 10:00' is a clean 422 server-side, so it is a
 * null here and the user hears about it before the round trip.
 *
 * A blank `time` alongside a date is a **refusal, not a midnight default**.
 * Turning an unanswered time input into 00:00 invents a claim on someone's
 * working day that nobody made; the caller should flag the time field instead.
 * Pass '00:00' explicitly when midnight is genuinely what was chosen.
 */
export function toDueAtString(date: string, time: string): string | null {
  const day = typeof date === 'string' ? date.trim() : '';
  const clock = typeof time === 'string' ? time.trim() : '';
  if (!day || !clock) return null;
  if (!isRealDate(day)) return null;

  const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(clock);
  if (!match) return null;
  const [, hours = '', minutes = ''] = match;
  if (Number(hours) > 23 || Number(minutes) > 59) return null;

  return `${day} ${hours}:${minutes}`;
}

/**
 * Split a stored `due.local` back into the date and time a form edits.
 *
 * Text slicing, deliberately. `local` is a zone-less wall clock, so the round
 * trip form -> API -> form has to stay on strings; parsing it into a `Date` to
 * re-derive the parts would read it on the viewer's timeline and hand back a
 * different time to anyone in a different zone from the assignee.
 *
 * Empty strings for an undated or unparseable task, which is what an empty
 * `<input>` wants.
 */
export function fromDueLocal(due: TaskDue | null): { date: string; time: string } {
  const parts = due ? splitLocal(due.local) : null;
  return parts ?? { date: '', time: '' };
}

/**
 * The viewer's own zone, from the signed-in user, falling back sanely.
 *
 * `user.timezone` is NOT NULL server-side, but this tolerates absence for the
 * moments before the session loads. The fallback chain is the browser's
 * resolved zone, then 'UTC' — a wrong-looking clock is recoverable, a throw in
 * a list render is not. Zones are validated by construction rather than by
 * pattern: only the runtime knows which names it can actually format, and
 * 'Asia/Kolkata' — the server's default — is missing from
 * `Intl.supportedValuesOf('timeZone')` while formatting with it works fine.
 */
export function viewerZone(userTimeZone: string | null | undefined): string {
  const candidate = typeof userTimeZone === 'string' ? userTimeZone.trim() : '';
  if (candidate && wallClockFormatter(candidate)) return candidate;
  try {
    const resolved = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (resolved) return resolved;
  } catch {
    /* falls through to UTC */
  }
  return 'UTC';
}

/* ------------------------------------------------------------------ *
 * Labels.
 * ------------------------------------------------------------------ */

/** 'follow_up' -> 'Follow up'. Also covers a value added server-side before this map is. */
function humanise(value: string): string {
  const words = value.replace(/_/g, ' ').trim();
  if (!words) return '—';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const KIND_LABELS: Record<TaskKind, string> = {
  follow_up: 'Follow up',
  call: 'Call',
  email: 'Email',
  meeting: 'Meeting',
  document: 'Document',
  review: 'Review',
};

/**
 * Label for a TaskKind: 'follow_up' -> 'Follow up'.
 *
 * Kinds are presentational — no kind moves a stage or triggers anything — so
 * these are free to be plain words.
 */
export function taskKindLabel(kind: TaskKind): string {
  return KIND_LABELS[kind] ?? humanise(String(kind));
}

const STATE_LABELS: Record<TaskState, string> = {
  open: 'Open',
  done: 'Done',
  cancelled: 'Cancelled',
};

/**
 * Label for a TaskState.
 *
 * ⚠️ *SUPERSEDED 2026-10-12, and the retired half was an INSTRUCTION rather than
 * an observation, so it had to go:* "'cancelled' is treated as closed
 * everywhere, but **nothing produces it automatically** — it is a seam for
 * future automation. A manual Cancel control is fine; never word it as
 * something the system does on its own ("auto-cancelled", "cancelled by the
 * system"), because today nothing does."
 *
 * 🔴 SOMETHING DOES NOW. The ITSM task sync maps an ITSM *rejection* onto
 * 'cancelled' (owner's decision: done ↔ resolved, cancelled ↔ rejected), and it
 * arrives on a worker tick with nobody at a screen. So copy saying the system
 * never cancels anything is false, and following the retired instruction would
 * word the next screen wrongly with nothing failing.
 *
 * ⚠️ IT WAS ALSO ALWAYS SETTABLE BY A PERSON, which the old note obscured:
 * TaskFormModal offers Cancelled in edit mode, and PATCH /tasks/{id} accepts
 * the state. A task is always born 'open' — create accepts no state key at all,
 * so that refusal names a missing key and is not a refusal of the value.
 *
 * ✅ THE HALF THAT SURVIVES, FROM ITS OWN REASON RATHER THAN FROM THE DEAD ONE:
 * 'done' and 'cancelled' are both closed and are OPPOSITE OUTCOMES — the work
 * happened, or it was called off. ⛔ So no surface may give the two one shared
 * word. A joint "Closed" label makes delivered work indistinguishable from
 * abandoned work on every list in the app, and that is true whoever or whatever
 * did the cancelling.
 *
 * ⚠️ A cancelled task may carry NO completer and no completion time — ordinary
 * for an ITSM-born rejection, since nobody here cancelled it. Do not render
 * that absence as missing data, and do not read it as provenance: `origin` is
 * the field that answers where a task came from.
 */
export function taskStateLabel(state: TaskState): string {
  return STATE_LABELS[state] ?? humanise(String(state));
}
